'use strict';

import { Entity } from '../Entity';
import { User } from './User';

export class UserMeta extends Entity {
  protected id: number | null;
  protected user: User;
  protected key: string;
  protected valueString: string | null;
  protected valueInt: number | null;
  protected valueBool: boolean | null;
  protected valueJson: any | null;
  protected updatedAt: Date | null;

  /**
   * Hydrate the entity from a plain object.
   * @param {any} data - Raw object with fields matching the UserMeta schema.
   * @returns {void}
   */
  public hydrate(data: any): void {
    this.id = data.id ?? this.id ?? null;
    this.key = data.key ?? this.key ?? '';
    this.valueString = data.valueString ?? null;
    this.valueInt = typeof data.valueInt === 'number' ? data.valueInt : null;
    this.valueBool = typeof data.valueBool === 'boolean' ? data.valueBool : null;
    this.valueJson = data.valueJson ?? null;
    this.updatedAt = data.updatedAt ? new Date(data.updatedAt) : (this.updatedAt ?? null);

    if (data.user) {
      const user = new User();
      user.hydrate(data.user);
      this.user = user;
    }
  }

  /** @returns {number|null} The primary identifier. */
  public getId(): number | null {
    return this.id ?? null;
  }

  /**
   * @param {number|null} id - The primary identifier.
   * @returns {this} Fluent self.
   */
  public setId(id: number | null): this {
    this.id = id;
    return this;
  }

  /** @returns {User} The owning user. */
  public getUser(): User {
    return this.user;
  }

  /**
   * @param {User} user - The owning user.
   * @returns {this} Fluent self.
   */
  public setUser(user: User): this {
    this.user = user;
    return this;
  }

  /** @returns {string} The metadata key (e.g., "favouriteColour"). */
  public getKey(): string {
    return this.key;
  }

  /**
   * @param {string} key - The metadata key.
   * @returns {this} Fluent self.
   */
  public setKey(key: string): this {
    this.key = key;
    return this;
  }

  /** @returns {string|null} The string value if stored as string. */
  public getValueString(): string | null {
    return this.valueString ?? null;
  }

  /**
   * @param {string|null} value - String value to store.
   * @returns {this} Fluent self.
   */
  public setValueString(value: string | null): this {
    this.resetValues();
    this.valueString = value;
    return this;
  }

  /** @returns {number|null} The integer value if stored as number. */
  public getValueInt(): number | null {
    return this.valueInt ?? null;
  }

  /**
   * @param {number|null} value - Integer value to store.
   * @returns {this} Fluent self.
   */
  public setValueInt(value: number | null): this {
    this.resetValues();
    this.valueInt = typeof value === 'number' ? value : null;
    return this;
  }

  /** @returns {boolean|null} The boolean value if stored as boolean. */
  public getValueBool(): boolean | null {
    return this.valueBool ?? null;
  }

  /**
   * @param {boolean|null} value - Boolean value to store.
   * @returns {this} Fluent self.
   */
  public setValueBool(value: boolean | null): this {
    this.resetValues();
    this.valueBool = typeof value === 'boolean' ? value : null;
    return this;
  }

  /** @returns {any|null} The JSON value if stored as object/array. */
  public getValueJSON(): any | null {
    return this.valueJson ?? null;
  }

  /**
   * @param {any|null} value - JSON-serializable value (object/array) to store.
   * @returns {this} Fluent self.
   */
  public setValueJSON(value: any | null): this {
    this.resetValues();
    this.valueJson = value ?? null;
    return this;
  }

  /** @returns {Date|null} The last update timestamp. */
  public getUpdatedAt(): Date | null {
    return this.updatedAt ?? null;
  }

  /**
   * @param {Date|string|null} date - Date instance or ISO string; null to clear.
   * @returns {this} Fluent self.
   */
  public setUpdatedAt(date: Date | string | null): this {
    if (date instanceof Date) {
      this.updatedAt = date;
    } else if (typeof date === 'string') {
      this.updatedAt = new Date(date);
    } else {
      this.updatedAt = null;
    }
    return this;
  }

  /**
   * Convenience getter returning the stored value in priority order:
   * JSON > boolean > integer > string.
   * @returns {any|null} The stored value or null.
   */
  public getValue(): any | null {
    if (this.valueJson !== null && this.valueJson !== undefined) return this.valueJson;
    if (this.valueBool !== null && this.valueBool !== undefined) return this.valueBool;
    if (this.valueInt !== null && this.valueInt !== undefined) return this.valueInt;
    return this.valueString ?? null;
  }

  /**
   * Clears all value fields before setting a new typed value.
   * @returns {void}
   */
  protected resetValues(): void {
    this.valueString = null;
    this.valueInt = null;
    this.valueBool = null;
    this.valueJson = null;
  }
}
